import { useEffect } from 'react';
import {
  Button,
  PasswordInput,
  PhoneNumberInput,
  Stack,
  Text,
  TextInput,
} from '@adc/parallax-component-library';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { useUserActivation } from '~/api/query/userActivationData';
import { useUserActivationUpdate } from '~/api/query/userActivationUpdate';
import { userAccountInformationSchema } from '~/features/activation/validations';
import { useModal } from '~/features/modals/useModal';
import { useLogin } from '~/hooks/useLogin';
import { usePasswordValidation } from '~/hooks/usePassword';
import { buildTestId } from '~/utils/buildTestId';
import { publicRoute } from '~/utils/routesAuthorization';

const testID = 'activation-flow-account';

export const Route = createFileRoute('/activation/$token/account')({
  component: RouteComponent,
  beforeLoad: publicRoute,
});

function RouteComponent() {
  const navigate = useNavigate();
  const { token } = Route.useParams();

  const userActivationPersonalData = useUserActivationUpdate();

  const { data: tokenData, isLoading } = useUserActivation({
    token,
  });

  const { openModal } = useModal();

  const passwordSchema = usePasswordValidation({
    hasNewPassword: true,
    validateCurrentPassword: false,
    user: {
      firstName: tokenData?.firstName ?? '',
      lastName: tokenData?.lastName ?? '',
      email: tokenData?.email ?? '',
    },
    // This Translations are based on Figma content provided
    translations: {
      password: {
        required: 'Required',
        minLength: () => 'Must be at least 12 characters',
        maxLength: ({ count }) => `Must under ${count} characters`,
        trim: 'Must not begin or end with a space',
        repetitiveSequence: () => `That password is too repetitive`,
        context: 'That password is too easy to guess',
        dictionary: 'Password cannot be a commonly known password.',
        repeatRequired: 'Required',
        mustMatch: 'Password must match',
      },
    },
  });

  const handleBack = () => {
    void navigate({ to: `/activation/${token}/legal`, replace: true });
  };

  const { login, isLoginPending } = useLogin({
    onSuccess: async () => {
      // Redirect & modal
      await navigate({ to: '/', replace: true });
      openModal('AccountActivated', { testID: 'activated-account' });
    },
  });

  const form = useForm({
    defaultValues: {
      id: tokenData?.id ?? '',
      email: tokenData?.email ?? '',
      phone: tokenData?.phone ?? '',
      firstName: tokenData?.firstName ?? '',
      lastName: tokenData?.lastName ?? '',
      password: '',
      repeatPassword: '',
    },
    validators: {
      onChange: userAccountInformationSchema.and(passwordSchema),
    },
    onSubmit: async ({ value }) => {
      const { email, password, phone } = value;
      // Update Password

      const response = await userActivationPersonalData.mutateAsync({
        id: tokenData?.id ?? '',
        phone,
        password,
      });

      if (!response.id) {
        throw new Error('Failed to update user activation data');
      }

      // Login User

      await login({
        email,
        password,
      });
    },
  });

  useEffect(() => {
    // If no data for the form, invalid token
    if (!isLoading && !tokenData?.id) {
      void navigate({ to: '/', replace: true });
    }
  }, [isLoading, navigate, tokenData]);

  return (
    <Stack
      borderWidth="$0"
      maxWidth={800}
      width="100%"
      borderRadius="$1.5"
      backgroundColor="$surface.container"
      padding="$6"
      marginHorizontal="auto"
      marginTop="$9"
      gap="$6"
    >
      <Text type="$h3.medium">Account information</Text>
      <Text
        type="$bodyBase.default"
        testID={buildTestId({
          testID,
          suffix: ['user', 'role'],
        })}
      >
        You’ve been invited to join the LactateView team as a{' '}
        <span>{tokenData?.role ?? ''}</span>.
      </Text>
      <Stack flexDirection="column" gap="$6">
        <Stack
          flexDirection="row"
          gap="$6"
          $xs={{
            flexDirection: 'column',
          }}
        >
          <Stack flex={1} flexShrink={1} flexBasis="0%">
            <form.Field name="firstName">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'first', 'name'],
                  })}
                  disabled
                  aria-label="First Name"
                  label="First Name"
                  placeholder="First Name"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  autoCapitalize="words"
                  autoCorrect={false}
                  error={
                    field.state.meta.isTouched && !field.state.meta.isValid
                  }
                  supportingText={
                    field.state.meta.isTouched && !field.state.meta.isValid
                      ? field.state.meta.errors[0]?.message
                      : undefined
                  }
                />
              )}
            </form.Field>
          </Stack>
          <Stack flex={1} flexShrink={1} flexBasis="0%">
            <form.Field name="lastName">
              {(field) => (
                <TextInput
                  testID={buildTestId({
                    testID,
                    suffix: ['input', 'last', 'name'],
                  })}
                  aria-label="Last Name"
                  label="Last Name"
                  disabled
                  placeholder="Last Name"
                  value={field.state.value}
                  onChangeText={field.handleChange}
                  onBlur={field.handleBlur}
                  autoCapitalize="words"
                  autoCorrect={false}
                  error={
                    field.state.meta.isTouched && !field.state.meta.isValid
                  }
                  supportingText={
                    field.state.meta.isTouched && !field.state.meta.isValid
                      ? field.state.meta.errors[0]?.message
                      : undefined
                  }
                />
              )}
            </form.Field>
          </Stack>
        </Stack>

        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="phone">
            {(field) => (
              <PhoneNumberInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'phone'],
                })}
                label="Phone Number (optional)"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="tel"
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
        <Text type="$h4.medium" color="$color.gray11Dark">
          Login information
        </Text>
        <Stack flex={1} flexShrink={1} flexBasis="0%">
          <form.Field name="email">
            {(field) => (
              <TextInput
                testID={buildTestId({
                  testID,
                  suffix: ['input', 'email'],
                })}
                aria-label="Email"
                label="Email"
                placeholder="Email"
                value={field.state.value}
                onChangeText={field.handleChange}
                onBlur={field.handleBlur}
                inputMode="email"
                autoCapitalize="none"
                autoCorrect={false}
                error={field.state.meta.isTouched && !field.state.meta.isValid}
                disabled
                supportingText={
                  field.state.meta.isTouched && !field.state.meta.isValid
                    ? field.state.meta.errors[0]?.message
                    : undefined
                }
              />
            )}
          </form.Field>
        </Stack>
        <Stack
          flexDirection="row"
          gap="$6"
          $xs={{
            flexDirection: 'column',
          }}
        >
          <Stack flex={1} flexBasis="50%">
            <form.Field name="password">
              {(field) => {
                const isValid = field.state.meta.isValid ? 'yes' : 'no';

                const message =
                  isValid == 'yes'
                    ? 'Password must be at least 12 characters'
                    : field.state.meta.errors[0]?.message;

                return (
                  <PasswordInput
                    aria-label="Password"
                    label="Password"
                    valid={field.state.meta.isTouched ? isValid : 'pending'}
                    supportingText={message}
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    ariaLabelExtractor={(error: boolean) =>
                      error ? 'Error, passwords do not match' : ''
                    }
                    hideButtonAriaLabelExtractor={(hidden: boolean) =>
                      hidden ? 'Show password' : 'Hide password'
                    }
                    testID={buildTestId({
                      testID,
                      suffix: 'password',
                    })}
                  />
                );
              }}
            </form.Field>
          </Stack>
          <Stack flex={1} flexBasis="50%">
            <form.Field name="repeatPassword">
              {(field) => {
                const arePasswordsMatching =
                  field.state.value === field.form.getFieldValue('password') &&
                  field.state.value.length > 0
                    ? 'yes'
                    : 'no';

                return (
                  <PasswordInput
                    aria-label="Repeat Password"
                    label="Repeat Password"
                    onChangeText={field.handleChange}
                    onBlur={field.handleBlur}
                    autoComplete="new-password"
                    autoCapitalize="none"
                    autoCorrect={false}
                    valid={
                      field.state.meta.isTouched
                        ? arePasswordsMatching
                        : 'pending'
                    }
                    supportingText="Passwords must be identical"
                    alwaysShowSupportingRow
                    ariaLabelExtractor={(error: boolean) =>
                      error ? 'Error, passwords do not match' : ''
                    }
                    hideButtonAriaLabelExtractor={(hidden: boolean) =>
                      hidden ? 'Show password' : 'Hide password'
                    }
                    testID={buildTestId({
                      testID,
                      suffix: ['repeat', 'password'],
                    })}
                  />
                );
              }}
            </form.Field>
          </Stack>
        </Stack>
      </Stack>
      <Stack
        marginTop="$4"
        flexDirection="row"
        gap="$4"
        justifyContent="flex-end"
      >
        <Button
          // @ts-expect-error Parallax doesn't type Button minWidth
          minWidth={164}
          label="Back"
          type="button"
          variant="secondary"
          onPress={handleBack}
          testID={buildTestId({
            testID,
            suffix: 'back',
          })}
        />
        <form.Subscribe selector={(state) => [state.isSubmitting]}>
          {([isSubmitting]) => (
            <Button
              type="submit"
              variant="primary"
              // @ts-expect-error Parallax doesn't type Button minWidth
              minWidth={164}
              label={isSubmitting || isLoginPending ? 'Sending...' : 'Complete'}
              disabled={isSubmitting}
              onPress={() => {
                void form.handleSubmit();
              }}
              testID={buildTestId({
                testID,
                suffix: 'submit',
              })}
            />
          )}
        </form.Subscribe>
      </Stack>
    </Stack>
  );
}
